import moment from 'moment';

export type TalentReviewPathOptions = {
	groupID?: string;
	templateID?: string | null;
	localDate?: string;
	momentID?: string | null;
	snapshotID?: string;
	/** Isolated person on the grid; kept when switching days and versions. */
	userID?: string;
	/** Opens the template editor for a new grid. */
	newTemplate?: boolean;
	/** Extra query string to keep (with or without a leading `?`). */
	search?: string;
};

const ROOT = '/talent-reviews/v2';

const searchParams = (search: string) =>
	new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

/**
 * Isolated person encoded in a talent review URL, if any.
 * @param search query string, with or without a leading `?`
 */
export const talentReviewUserIDFromSearch = (search: string): string | undefined =>
	searchParams(search).get('userID') ?? undefined;

/**
 * Sets or clears the isolated person on a talent review query string.
 * @param search query string, with or without a leading `?`
 * @param userID the person to isolate, or nobody
 */
export const talentReviewSearchWithUserID = (search: string, userID?: string): string => {
	const params = searchParams(search);
	if (userID) {
		params.set('userID', userID);
	} else {
		params.delete('userID');
	}

	return params.toString();
};

const withQuery = (pathname: string, options: TalentReviewPathOptions) => {
	const params = searchParams(options.search ?? '');

	if (options.momentID) {
		params.set('moment_id', options.momentID);
	}

	if (options.userID) {
		params.set('userID', options.userID);
	}

	const query = params.toString();
	return query ? `${pathname}?${query}` : pathname;
};

/**
 * Builds a talent review v2 URL. Pass whatever you know; missing fields fall
 * back to the next broader page (grid → group → groups list).
 * @param options the ids and search to build the URL from
 */
export const talentReviewPath = (options: TalentReviewPathOptions = {}) => {
	if (options.snapshotID) {
		return withQuery(`${ROOT}/snapshots/${options.snapshotID}`, options);
	}

	if (options.newTemplate) {
		const params = new URLSearchParams(
			options.search?.startsWith('?') ? options.search.slice(1) : (options.search ?? ''),
		);
		if (options.groupID) {
			params.set('groupID', options.groupID);
		}
		if (options.momentID) {
			params.set('moment_id', options.momentID);
		}
		const query = params.toString();
		return query ? `${ROOT}/templates/new?${query}` : `${ROOT}/templates/new`;
	}

	if (options.groupID && options.templateID) {
		const localDate = options.localDate ?? moment().format('YYYY-MM-DD');
		return withQuery(
			`${ROOT}/groups/${options.groupID}/${options.templateID}/${localDate}`,
			options,
		);
	}

	if (options.groupID) {
		return withQuery(`${ROOT}/groups/${options.groupID}`, options);
	}

	if (options.templateID) {
		return withQuery(`${ROOT}/templates/${options.templateID}`, options);
	}

	return withQuery(`${ROOT}/groups`, options);
};

/**
 * Returns {@link talentReviewPath} so you can build links from clicks and maps
 * without breaking the rules of hooks.
 */
export default function useTalentReviewPath() {
	return talentReviewPath;
}
